import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SearchPage from '@/components/Pages/SearchPage';
import { useAgentStore, useAuthStore, useModalStore } from '@/store';
import { AGENT_ICON } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import ListItem from '@/components/ListItem';

const styles = StyleSheet.create({
  emptyRoot: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  emptyIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 20,
    textAlign: 'center',
  },
  emptySubtitle: {
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.9,
  },
});

const AgentSearchPage = React.memo(() => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const agents = useAgentStore(state => state.agents);
  const recentAgentIds = useAgentStore(state => state.recentAgentIds);
  const searchResultIds = useAgentStore(state => state.searchResultIds);
  const isLoadingAgentSearch = useAgentStore(state => state.isLoadingAgentSearch);
  const isLoadingRecentAgents = useAgentStore(state => state.isLoadingRecentAgents);
  const searchError = useAgentStore(state => state.searchError);
  // Actions
  const fetchRecentAgents = useAgentStore(state => state.fetchRecentAgents);
  const searchAgents = useAgentStore(state => state.searchAgents);
  const resetSearch = useAgentStore(state => state.resetSearch);
  const { openModal } = useModalStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();

  // Recent agents are a per-user list — skip the fetch for anonymous
  // visitors (would 401) and hide the "Recent" rail via showRecent below.
  React.useEffect(() => {
    if (hasUser) fetchRecentAgents();
  }, [fetchRecentAgents, hasUser]);

  // Memoize modal handlers — editing a custom agent is owner-only, so we
  // gate the entry point even though the badge is only shown for owners
  // (defense in depth in case the flag is stale for anonymous browsers).
  const handleEditModal = useCallback((agentId) => (e) => {
    e.stopPropagation();
    requireAuth(
      () => openModal('CustomAgentModal', {
        key: `agent-edit-${agentId}`,
        agentId,
      }),
      {
        source: 'agents_search_edit',
        title: 'Sign in to edit your bots',
        subtitle: 'Your bots are saved to your account.',
        icon: 'user-pen',
      }
    );
  }, [openModal, requireAuth]);

  const handleDetailsModal = useCallback((agentId) => {
    openModal(
      'AgentDetailsModal',
      {
        key: `agent-details-${agentId}`,
        agentId,
      },
      { 
        width: isXS ? '100%' : 600, 
        fullScreen: isXS
      }
    );
  }, [openModal, isXS]);

  // Memoize badges creation
  const createAgentBadges = useCallback((agent) => {
    if (!agent.isOwner) return [];
    
    return [
      {
        text: t('pages.agents.mine'),
        backgroundColor: colors.accent_color_primary,
        textColor: 'accent'
      },
      {
        icon: 'user-pen',
        onPress: handleEditModal(agent.id)
      }
    ];
  }, [colors, handleEditModal, t]);

  const renderAgentItem = useCallback((agentId) => {
    const agent = agents[agentId];
    if (!agent) return null;

    const badges = createAgentBadges(agent);
    const subtitle = (
      <Text color='secondary' size='m' numberOfLines={1}>{agent.description}</Text>
    );

    return (
      <ListItem
        key={agent.id}
        isRounded={true}
        imageUrl={agent.avatarPath}
        iconName={AGENT_ICON}
        title={agent.name}
        subtitle={subtitle}
        badges={badges}
        isXS={isXS}
        onPress={() => handleDetailsModal(agent.id)}
      />
    );
  }, [agents, isXS, createAgentBadges, handleDetailsModal]);

  const renderEmpty = useCallback(() => {
    const iconSize = 40;
    const iconColor = colors.text_color_tertiary;
    return (
      <View style={[styles.emptyRoot, { paddingVertical: 48 }]}>
        <View style={[styles.emptyIconWrap, { backgroundColor: colors.solid_color_tertiary }]}>
          <Icon name="robot" size={iconSize} color={iconColor} />
        </View>
        <Text
          size="l"
          weight="semibold"
          color="primary"
          style={styles.emptyTitle}
        >
          {t('pages.agents.searchEmptyTitle')}
        </Text>
        <Text
          size="m"
          color="secondary"
          style={styles.emptySubtitle}
        >
          {t('pages.agents.searchEmptySubtitle')}
        </Text>
      </View>
    );
  }, [colors, t]);

  const searchPageProps = useMemo(() => ({
    title: t('pages.agents.searchTitle'),
    description: "Find a bot for your next job. Search by name, specialty, or category.",
    searchPlaceholder: t('pages.agents.searchPlaceholderFull'),
    cancelRoute: "/agents",
    isSearching: isLoadingAgentSearch,
    searchError,
    searchResults: searchResultIds,
    onSearch: searchAgents,
    onClearSearch: resetSearch,
    renderItem: renderAgentItem,
    // Match the backend: /json/agents/search rejects queries > 100 chars.
    maxLength: 100,
    showRecent: hasUser,
    recentItems: recentAgentIds,
    isLoadingRecent: isLoadingRecentAgents,
    emptyMessage: t('pages.agents.searchEmptyMessage'),
    renderEmpty,
  }), [
    t,
    isLoadingAgentSearch, 
    searchError, 
    searchResultIds, 
    searchAgents, 
    resetSearch, 
    renderAgentItem,
    recentAgentIds,
    isLoadingRecentAgents,
    renderEmpty,
    hasUser,
  ]);

  return <SearchPage {...searchPageProps} />;
});

function AgentSearchRoute() {
  return (
    <AgentSearchPage />
  );
}

export default React.memo(AgentSearchRoute);